import { useMemo, useCallback } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { logout } from '../store';

import {
  AuthType,
  ReportResponse,
  Notification,
  NotificationRequest,
  Comment,
  EmargementResponse,
} from '../types';
import { AnomalieType, Service } from '../types/theme';
import { useAuth } from './auth.hook';

const apiVersion = 'v1';

export const useApi = () => {
  const { auth } = useAuth();
  const dispatch = useDispatch();

  const instanceAxios = useMemo(() => {
    const instance = axios.create({
      baseURL: process?.env.REACT_APP__API_URL,
      headers: auth?.token ? { Authorization: `Bearer ${auth.token}` } : {},
    });
    instance.interceptors.response.use(
      (response) => {
        return response && response.data;
      },
      (error) => {
        const { status } = error.response;
        if (status !== 401) {
          throw error;
        }
        dispatch(logout());
      }
    );
    return instance;
  }, [auth, dispatch]);

  const login = useCallback(
    (username: string, password: string): Promise<AuthType> =>
      instanceAxios.post('/api/Identity/login', {
        username,
        password,
      }),
    [instanceAxios]
  );

  const updatePassword = useCallback(
    (username: string, password: string, newPassword: string) =>
      instanceAxios.post('/api/Identity/updatePassword', {
        username,
        password,
        newPassword,
      }),
    [instanceAxios]
  );

  const resetPassword = useCallback(
    (userName: string) => instanceAxios.post('/api/Identity/resetPassword', { userName }),
    [instanceAxios]
  );

  const getAnomalies = useCallback(
    (themeShortCode: string): Promise<AnomalieType[]> =>
      instanceAxios.get(`/api/${apiVersion}/Operation/getAnomalies/${themeShortCode}`),
    [instanceAxios]
  );

  const createReport = useCallback(
    (report: any) => instanceAxios.post(`/api/${apiVersion}/Report/createReport`, { ...report }),
    [instanceAxios]
  );

  const getReport = useCallback(
    (filtres: any): Promise<ReportResponse> =>
      instanceAxios.post(`/api/${apiVersion}/Report/getReport`, { ...filtres }),
    [instanceAxios]
  );

  const updateReport = useCallback(
    (report: any) => instanceAxios.post(`/api/${apiVersion}/Report/updateReport`, { ...report }),
    [instanceAxios]
  );

  const updateReportStatus = useCallback(
    (data: any) => instanceAxios.post(`/api/${apiVersion}/Report/updateReportStatus`, { ...data }),
    [instanceAxios]
  );

  const deleteReport = useCallback(
    (reportId: number | string) =>
      instanceAxios.post(`/api/${apiVersion}/Report/deleteReport`, { reportId }),
    [instanceAxios]
  );

  const exportReports = useCallback(
    (filtres: any) =>
      instanceAxios.post(
        `/api/${apiVersion}/Report/exportReports`,
        { ...filtres },
        { responseType: 'arraybuffer' }
      ),
    [instanceAxios]
  );

  const shareReport = useCallback(
    ({
      emails,
      reportId,
      themeShortCode,
      comments,
    }: {
      emails: string[];
      reportId: string;
      themeShortCode: string;
        comments: string
    }) =>
      instanceAxios.post(`/api/${apiVersion}/Report/shareReport`, {
        reportId,
        themeShortCode,
        emails,
        comments,
      }),

    [instanceAxios]
  );

  const getSumUnreadReport = useCallback(
    () => instanceAxios.get(`/api/${apiVersion}/Report/getSumUnreadReport`),
    [instanceAxios]
  );

  const getAttachedPiece = useCallback(
    ({ reportId, name }: { reportId: string; name: string }) =>
      instanceAxios.get(`/api/${apiVersion}/Report/getAttachedPiece/${reportId}/${name}`, {
        responseType: 'blob',
      }),
    [instanceAxios]
  );

  const getAttachedPieceByHash = useCallback(
    ({ reportId, name, hash }: { reportId: string; name: string; hash: string }) =>
      instanceAxios.get(
        `/api/${apiVersion}/Report/getAttachedPieceByHash/${reportId}/${name}/${hash}`,
        {
          responseType: 'blob',
        }
      ),
    [instanceAxios]
  );

  const getServicesByDate = useCallback(
    (circulationDate: string): Promise<Service[]> =>
      instanceAxios.get(`/api/${apiVersion}/Data/GetServicesByDate/${circulationDate}`),
    [instanceAxios]
  );

  const getResidences = useCallback(
    () => instanceAxios.get(`/api/${apiVersion}/Operation/getResidence`),
    [instanceAxios]
  );

  const getNotifications = useCallback(
    (): Promise<Notification[]> =>
      instanceAxios.get(`/api/${apiVersion}/Notification/getNotification`),
    [instanceAxios]
  );

  const updateNotifications = useCallback(
    (data: NotificationRequest) =>
      instanceAxios.post(`/api/${apiVersion}/Notification/updateNotification`, data),
    [instanceAxios]
  );

  const deleteNotifications = useCallback(
    () => instanceAxios.delete(`/api/${apiVersion}/Notification/deleteNotification`),
    [instanceAxios]
  );

  const addComment = useCallback(
    (data: { userName: string; reportId: number; comment: string }) =>
      instanceAxios.post(`/api/${apiVersion}/Comment/addComment`, data),
    [instanceAxios]
  );

  const getComments = useCallback(
    (reportId: number): Promise<Comment[]> =>
      instanceAxios.post(`/api/${apiVersion}/Comment/getComment`, { reportId, Comment: 'fix' }),
    [instanceAxios]
  );

  const addEmargement = useCallback(
    (data: { userName: string; title: string; startDate: string; description: string }) =>
      instanceAxios.post(`/api/${apiVersion}/Emargement/addEmargement`, data),
    [instanceAxios]
  );

  const getEmargement = useCallback(
    (data: {
      connectedUserCPCode?: string;
      isValidated?: boolean;
      pageNumber?: number;
      maxResultPage?: number;
      id?: string | number;
    }): Promise<EmargementResponse> =>
      instanceAxios.post(`/api/${apiVersion}/Emargement/getEmargement`, data),
    [instanceAxios]
  );

  const validateEmargement = useCallback(
    (emargementId: number | string) =>
      instanceAxios.post(`/api/${apiVersion}/Emargement/validate`, { emargementId }),
    [instanceAxios]
  );

  const getAgentNecByService = useCallback(
    (serviceName: number | string) =>
      instanceAxios.get(`/api/${apiVersion}/Operation/getAgentNECByService/${serviceName}`),
    [instanceAxios]
  );

  const getManagerList = useCallback(
    () =>
      instanceAxios.get(`/api/Identity/getAllManager`),
    [instanceAxios]
  );

  const getEnabledProducts = useCallback(
    () => instanceAxios.get(`api/v1/Operation/getEnabledProducts`),
    [instanceAxios]
  );

  const getUsersNameAndLastName = useCallback(
    () => instanceAxios.get(`/api/Identity/getUsersNameAndLastName`),
    [instanceAxios]
  );

  return {
    login,
    updatePassword,
    resetPassword,
    getAnomalies,
    createReport,
    getReport,
    getServicesByDate,
    getResidences,
    updateReport,
    updateReportStatus,
    deleteReport,
    exportReports,
    shareReport,
    getSumUnreadReport,
    getAttachedPiece,
    getAttachedPieceByHash,
    getNotifications,
    updateNotifications,
    deleteNotifications,
    addComment,
    getComments,
    addEmargement,
    getEmargement,
    validateEmargement,
    getAgentNecByService,
    getManagerList,
    getEnabledProducts,
    getUsersNameAndLastName,
  };
};
